import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { api, type BuildRow } from '../lib/api.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useSortable } from '../hooks/useSortable.js';
import { SortableTh } from '../components/SortableTh.js';
import { StatCard } from '../components/StatCard.js';
import { PageHeader } from '../components/PageHeader.js';
import { EmptyState } from '../components/EmptyState.js';

type BuildStatus = 'known' | 'unreachable' | 'unknown';

const statusDot: Record<BuildStatus, string> = {
  known: 'var(--accent-green)',
  unreachable: 'var(--accent-red)',
  unknown: 'var(--text-dimmed)',
};

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function BuildsPage() {
  const navigate = useNavigate();
  usePageTitle('Cerbo Builds');
  const [filter, setFilter] = useState<BuildStatus | 'all'>('all');
  const { sortKey, sortDir, toggle, sort } = useSortable<'namespace' | 'region' | 'status' | 'lastSeenAt'>('namespace');

  const { data, isLoading } = useQuery({
    queryKey: ['builds'],
    queryFn: () => api.listBuilds(),
  });

  const builds: BuildRow[] = (data?.builds ?? []) as BuildRow[];
  const filtered = filter === 'all' ? builds : builds.filter((b) => b.status === filter);
  const visible = sort(filtered, (b) => {
    if (sortKey === 'lastSeenAt') return b.lastSeenAt ?? '';
    return b[sortKey] ?? '';
  });

  const counts = {
    known: builds.filter((b) => b.status === 'known').length,
    unreachable: builds.filter((b) => b.status === 'unreachable').length,
    unknown: builds.filter((b) => b.status === 'unknown').length,
  };

  const lastSyncedAt = builds.reduce<string | null>((latest, b) => {
    if (!b.probedAt) return latest;
    if (!latest) return b.probedAt;
    return b.probedAt > latest ? b.probedAt : latest;
  }, null);

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Kubernetes"
        title="Cerbo Builds"
        actions={
          <span className="text-xs text-text-dimmed">
            Last synced: {timeAgo(lastSyncedAt)}
          </span>
        }
      />

      <div className="shrink-0 px-6 py-3 flex items-center gap-4 overflow-x-auto">
        <StatCard label="Total" value={builds.length} />
        <StatCard label="Known" value={counts.known} />
        <StatCard label="Unreachable" value={counts.unreachable} />
        <StatCard label="Unknown" value={counts.unknown} />
      </div>

      {/* Filter tabs */}
      <div className="shrink-0 px-6 pb-3 flex gap-1.5">
        {(['all', 'known', 'unreachable', 'unknown'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className="rounded-lg px-3 py-1.5 text-xs font-semibold capitalize tracking-wide transition-all duration-150"
            style={filter === f ? {
              background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)',
              color: '#fff',
              boxShadow: '0 2px 8px rgba(13,207,184,0.35)',
            } : {
              background: 'var(--glass-bg)',
              backdropFilter: 'blur(8px)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--glass-border)',
            }}
          >
            {f === 'all' ? `All · ${builds.length}` : `${f} · ${counts[f]}`}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-32 text-sm text-text-dimmed">Loading…</div>
        ) : builds.length === 0 ? (
          <EmptyState
            icon="🏗️"
            title="No builds found"
            description="The infra sync worker will populate builds automatically. Check that GITHUB_TOKEN is configured."
          />
        ) : (
          <div className="glass rounded-2xl overflow-hidden" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <SortableTh label="Namespace" sortKey="namespace" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} />
                  <SortableTh label="Region" sortKey="region" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} />
                  <SortableTh label="Status" sortKey="status" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} />
                  <SortableTh label="Last seen" sortKey="lastSeenAt" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} />
                </tr>
              </thead>
              <tbody>
                {visible.map((b) => {
                  const status = b.status as BuildStatus;
                  return (
                    <tr
                      key={b.id}
                      className="border-b border-border last:border-0 cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={() => void navigate({ to: '/builds/$id', params: { id: b.id } })}
                    >
                      <td className="px-5 py-3">
                        <div className="font-mono text-xs text-text-primary">{b.namespace}</div>
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest font-mono"
                          style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}
                        >
                          {b.region}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className="flex items-center gap-1.5 text-xs">
                          <span className="h-1.5 w-1.5 rounded-full" style={{ background: statusDot[status] }} />
                          <span style={{ color: statusDot[status] }}>{b.status}</span>
                        </span>
                      </td>
                      <td className="px-5 py-3 text-xs text-text-dimmed font-mono">{timeAgo(b.lastSeenAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
